import { ReactElement, useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Devices from '../components/Devices';
import RawData from '../components/Data/Raw-data.page';
import Analytics from '../components/Data/Analytics.page';
import DataActionHeader from '../components/Data/ActionHeader';
import { VIEW_FLOOR, VIEW_TABLE } from '../constants/global-constants';
import { ParametersType, TimeSpan, TimeSpanDateAttributes } from '../constants/telosair-interfaces';
import { handleTimeSpanChange } from '../_helpers/dashboardHelper';
import { useData } from '../context/data.context';
import { Filters, getAppliedFilter, setAppliedFilter } from '../services/localstorage.service';
import PlanGate from '../components/PlanGate';

export const TAB_RAW_DATA = 'raw-data';
export const TAB_DEVICES = 'devices';
export const TAB_ANALYTICS = 'analytics';

/**
 * Data component which loads the list of devices and data related to it.
 * @returns - A React element that represents the data component.
 */
export default function Data(): ReactElement {
  const { devices, loading, fetchSummary } = useData();
  const [params] = useSearchParams({});
  const SEARCH_PARAM_TAB = params.get('tab');
  const [selectedDevices, setSelectedDevices] = useState<{ label: string; value: number }[]>([]);
  const [selectedParameters, setSelectedParameters] = useState<ParametersType[]>(
    getAppliedFilter(Filters.dataParameters)?.data || ['hum']
  );
  const [refreshing, setRefreshing] = useState(false);
  const timeSpanFilter = getAppliedFilter(Filters.timeSpan)?.data;
  const [timeSpan, setTimeSpan] = useState(timeSpanFilter || TimeSpan.Hrs24);
  const [expandAll, setExpandAll] = useState(false);
  const [selectedViews, setSelectedViews] = useState([VIEW_TABLE]);
  const [lastSynced, setLastSynced] = useState<Date>(new Date());
  const [timeSpanDate, setTimeSpanDate] = useState<TimeSpanDateAttributes | null>(null);
  const navigate = useNavigate();
  useEffect(() => {
    if (!loading) {
      const stored: { label: string; value: number }[] | undefined = getAppliedFilter(
        Filters.dataDevices
      )?.data;
      // Keep only stored devices that still exist so a stale entry can't break the page.
      const validStored = (stored || []).filter((selected) =>
        devices.some((device) => device.serialNumber === selected.value)
      );
      setSelectedDevices(
        validStored.length > 0
          ? validStored
          : devices.slice(0, 2).map((item) => ({
              label: item.deviceName,
              value: item.serialNumber
            }))
      );
    }
  }, [loading, devices]);

  /**
   * Calls handleTimeSpanChange helper and also sets time span.
   * @param span - Time span selected.
   */
  const handleTimeChange = (span: string | TimeSpan): void => {
    setTimeSpan(span as TimeSpan);
    setAppliedFilter({ key: Filters.timeSpan, data: span });
    if (span !== TimeSpan.Custom) {
      setTimeSpanDate({ ...handleTimeSpanChange(span, timeSpanDate) });
    }
  };

  useEffect(() => {
    if (timeSpanDate === null) {
      setTimeSpanDate({ ...handleTimeSpanChange(timeSpan, timeSpanDate) });
    }
  }, [timeSpan, timeSpanDate]);

  useEffect(() => {
    if (timeSpanDate === null) {
      const time =
        timeSpanFilter === TimeSpan.Custom
          ? getAppliedFilter(Filters.timeSpanCustom)?.data
          : handleTimeSpanChange(timeSpan, timeSpanDate);
      setTimeSpanDate({ ...time });
    }
  }, [timeSpan, timeSpanDate]);

  /**
   * Function to get device, group and data summary.
   */
  const getAllSummaries = useCallback(async () => {
    if (timeSpanDate) {
      await fetchSummary(timeSpanDate.startDate, timeSpanDate.endDate);
    }
  }, [timeSpanDate]);

  /**
   * Function to refresh the data.
   */
  const onRefresh = async (): Promise<void> => {
    setTimeSpanDate({ ...handleTimeSpanChange(timeSpan, timeSpanDate) });
    setRefreshing(true);
    setLastSynced(new Date());
  };

  useEffect(() => {
    let refreshTimeout: any;
    if (refreshing && !loading) {
      refreshTimeout = setTimeout(() => {
        setLastSynced(new Date());
        setRefreshing(false);
      }, 3000);
    }
    return () => clearTimeout(refreshTimeout);
  }, [refreshing, loading]);

  /**
   * Function called on view change.
   * @param view - Selected view.
   */
  const onSelectView = (view: string): void => {
    if (view === VIEW_TABLE) {
      setSelectedViews([VIEW_TABLE]);
    } else {
      setSelectedViews([VIEW_TABLE, VIEW_FLOOR]);
    }
  };

  useEffect(() => {
    getAllSummaries();
  }, [getAllSummaries]);

  useEffect(() => {
    if (SEARCH_PARAM_TAB === TAB_ANALYTICS && selectedParameters.length > 1) {
      setSelectedParameters((prev) => [prev[0]]);
    }
  }, [params]);

  useEffect(() => {
    if (!SEARCH_PARAM_TAB) {
      // Redirect to /data?tab=react if the tab query param is missing
      navigate('/data?tab=raw-data', { replace: true });
    }
  }, [SEARCH_PARAM_TAB, navigate]);

  // const showAnalyticsGoogleForm = SEARCH_PARAM_TAB === TAB_ANALYTICS;

  return (
    <div className="flex flex-1 flex-col">
      <DataActionHeader
        currentView="data"
        onSelectDevices={(value) => {
          setSelectedDevices(value);
          setAppliedFilter({ key: Filters.dataDevices, data: value });
        }}
        onSelectParameters={(value) => {
          setSelectedParameters(value);
          setAppliedFilter({ key: Filters.dataParameters, data: value });
        }}
        selectedParameters={selectedParameters}
        selectedDevices={selectedDevices}
        selectedViews={selectedViews}
        onSelectView={(view) => onSelectView(view)}
        timeSpan={timeSpan}
        handleTimeChange={handleTimeChange}
        hasViewChangeButton={SEARCH_PARAM_TAB === TAB_DEVICES}
        onRefresh={() => onRefresh()}
        refreshing={refreshing}
        lastSynced={lastSynced}
        searchBox={SEARCH_PARAM_TAB === TAB_DEVICES}
        timeSpanDate={timeSpanDate}
        setTimeSpanDate={setTimeSpanDate}
        maxParameterCount={SEARCH_PARAM_TAB === TAB_ANALYTICS ? 1 : 20}
      />

      {SEARCH_PARAM_TAB === TAB_RAW_DATA && timeSpanDate && (
        <PlanGate requiredPlan="Basic">
          <RawData
            timeSpanDate={timeSpanDate}
            selectedDevices={selectedDevices}
            selectedParameters={selectedParameters}
            refreshing={refreshing}
            timeSpan={timeSpan}
          />
        </PlanGate>
      )}
      {SEARCH_PARAM_TAB === TAB_DEVICES && (
        <Devices
          expandAll={expandAll}
          setExpandAll={() => setExpandAll((prev) => !prev)}
          floorViewExpanded={selectedViews.includes(VIEW_FLOOR)}
          selectedDevices={selectedDevices.map((device) => device.value)}
          selectedParameters={selectedParameters}
        />
      )}
      {SEARCH_PARAM_TAB === TAB_ANALYTICS && timeSpanDate && (
        <PlanGate requiredPlan="Advanced">
          <Analytics
            timeSpanDate={timeSpanDate}
            selectedDevices={selectedDevices.map((device) => device.value)}
            selectedParameters={selectedParameters}
          />
        </PlanGate>
      )}
    </div>
  );
}
